import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAggregateActivity } from '@/features/audit';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Button } from '@/shared/ui/Button';
import { SidePanel } from '@/shared/ui/SidePanel';

import {
  useDecidePublication,
  usePlugin,
  usePublishPlugin,
  useRequestPublication,
  useReviewPlugin,
  useRevokePlugin,
} from '../api/marketplace';
import {
  advisoryFindings,
  blockingFindings,
  capabilityLabelKey,
  isTerminal,
  stageStates,
} from '../model/marketplace';

/* One submission on its way through review and publication, in a side panel.
 *
 * **The sandbox decides the review verdict**, so there is one button and no pass/fail choice: a
 * person starts the review and the sandbox compiles and runs the artifact.
 *
 * **Publication is two-eyes and each signature states its reason.** Approving asks for the reason
 * here because the server refuses a decision without one; the requester's own signature is refused
 * by the server and its sentence is shown where the button was pressed.
 *
 * **Revoking needs a reason and a second press**, because it stops the plugin for every tenant.
 */
export function ListingDrawer({
  pluginId,
  may,
  onClose,
}: {
  readonly pluginId: string;
  readonly may: { readonly review: boolean; readonly publish: boolean };
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const plugin = usePlugin(pluginId);
  const activity = useAggregateActivity('Plugin', pluginId);

  const review = useReviewPlugin();
  const request = useRequestPublication();
  const decide = useDecidePublication();
  const publish = usePublishPlugin();
  const revoke = useRevokePlugin();

  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [decisionReason, setDecisionReason] = useState('');
  const [now] = useState(() => new Date());

  const listing = plugin.data;
  if (listing === undefined) {
    return (
      <SidePanel label={t('marketplace.detail.panel')} onClose={onClose} head={null}>
        <p className="lq-mk__meta">
          {plugin.isError
            ? t('marketplace.error.pluginBody')
            : t('marketplace.detail.loading')}
        </p>
      </SidePanel>
    );
  }
  const stages = stageStates(listing.state, listing.review_verdict);
  const findings = [...blockingFindings(listing), ...advisoryFindings(listing)];

  const head = (
    <>
      <h2 className="lq-side__name">{listing.name}</h2>
      <div className="lq-side__sub">
        {listing.version} ·{' '}
        <span className="lq-mono" title={listing.artifact_hash}>
          {listing.artifact_hash.slice(0, 19)}…
        </span>
      </div>
    </>
  );

  return (
    <SidePanel label={listing.name} onClose={onClose} head={head}>
      <div className="lq-mk__drawer">
        <ol className="lq-mk__stages">
          {(['submitted', 'reviewed', 'published'] as const).map((stage) => (
            <li key={stage} className="lq-mk__stage" data-state={stages[stage]}>
              <span className="lq-mk__stageDot" aria-hidden="true" />
              {t(`marketplace.stage.${stage}`)}
            </li>
          ))}
        </ol>

        {isTerminal(listing.state) ? (
          <p className="lq-mk__meta">{t(`marketplace.terminal.${listing.state}`)}</p>
        ) : null}
        {findings.length > 0 ? (
          <div className="lq-mk__findings">
            {findings.map((finding) => (
              <div key={finding.code}>{finding.detail}</div>
            ))}
          </div>
        ) : null}

        <div className="lq-mk__legend">{t('marketplace.detail.declared')}</div>
        {listing.declared_capabilities.map((code) => (
          <div className="lq-mk__declared" key={code}>
            <span className="lq-mk__capName">
              {t(`marketplace.capName.${code.replace('.', '_')}`)}
            </span>
            <span className="lq-mk__grantDesc">{t(capabilityLabelKey(code))}</span>
          </div>
        ))}

        {may.review && listing.review_verdict === 'pending' ? (
          <section className="lq-mk__section">
            {review.isError ? (
              <p className="lq-mk__error">{review.error.message}</p>
            ) : null}
            <Button
              size="compact"
              className="lq-mk__start"
              busy={review.isPending}
              onClick={() => {
                review.mutate(pluginId);
              }}
            >
              {review.isPending
                ? t('marketplace.review.running')
                : t('marketplace.review.run')}
            </Button>
            <span className="lq-mk__fine">{t('marketplace.review.sandboxDecides')}</span>
          </section>
        ) : null}

        {may.publish &&
        listing.review_verdict === 'pass' &&
        listing.state === 'reviewed' ? (
          <section className="lq-mk__section">
            <div className="lq-mk__legend">
              {t('marketplace.listing.publicationTitle')}
            </div>
            <p className="lq-mk__meta">{t('marketplace.listing.twoEyesShort')}</p>
            <label className="lq-mk__field">
              {t('marketplace.listing.decisionReason')}
              <textarea
                className="lq-input lq-mk__textarea"
                rows={2}
                value={decisionReason}
                onChange={(event) => {
                  setDecisionReason(event.target.value);
                }}
              />
            </label>
            {request.isError ? (
              <p className="lq-mk__error">{request.error.message}</p>
            ) : null}
            {decide.isError ? (
              <p className="lq-mk__error">{decide.error.message}</p>
            ) : null}
            <div className="lq-mk__actions">
              <Button
                size="compact"
                busy={request.isPending}
                onClick={() => {
                  request.mutate(pluginId);
                }}
              >
                {t('marketplace.action.requestPublication')}
              </Button>
              <Button
                size="compact"
                tone="primary"
                disabled={decisionReason.trim() === ''}
                busy={decide.isPending}
                onClick={() => {
                  decide.mutate({
                    pluginId,
                    approve: true,
                    reason: decisionReason.trim(),
                  });
                }}
              >
                {t('marketplace.action.approve')}
              </Button>
            </div>
          </section>
        ) : null}

        {may.publish && listing.state === 'approved' ? (
          <section className="lq-mk__section">
            {publish.isError ? (
              <p className="lq-mk__error">{publish.error.message}</p>
            ) : null}
            <Button
              size="compact"
              tone="primary"
              className="lq-mk__start"
              busy={publish.isPending}
              onClick={() => {
                publish.mutate(pluginId);
              }}
            >
              {t('marketplace.action.publish')}
            </Button>
          </section>
        ) : null}

        {may.publish && listing.state === 'published' ? (
          <section className="lq-mk__section">
            <div className="lq-mk__legend">{t('marketplace.revoke.title')}</div>
            <p className="lq-mk__meta">{t('marketplace.revoke.short')}</p>
            <textarea
              className="lq-input lq-mk__textarea"
              rows={2}
              value={reason}
              aria-label={t('marketplace.revoke.reason')}
              placeholder={t('marketplace.revoke.reason')}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            {revoke.isError ? (
              <p className="lq-mk__error">{revoke.error.message}</p>
            ) : null}
            {revoking ? (
              <div className="lq-mk__confirm">
                <span className="lq-mk__spacer">{t('marketplace.revoke.ask')}</span>
                <Button
                  size="compact"
                  onClick={() => {
                    setRevoking(false);
                  }}
                >
                  {t('marketplace.action.keep')}
                </Button>
                <Button
                  size="compact"
                  tone="danger"
                  busy={revoke.isPending}
                  onClick={() => {
                    revoke.mutate(
                      { pluginId, reason: reason.trim() },
                      {
                        onSuccess: () => {
                          setRevoking(false);
                        },
                      },
                    );
                  }}
                >
                  {t('marketplace.action.revoke')}
                </Button>
              </div>
            ) : (
              <Button
                size="compact"
                className="lq-mk__start"
                disabled={reason.trim() === ''}
                onClick={() => {
                  setRevoking(true);
                }}
              >
                {t('marketplace.action.revoke')}
              </Button>
            )}
          </section>
        ) : null}

        {(activity.data?.items ?? []).length > 0 ? (
          <section className="lq-mk__section">
            <div className="lq-mk__legend">{t('marketplace.detail.activity')}</div>
            {(activity.data?.items ?? []).map((entry) => (
              <div
                className="lq-mk__actSmall"
                key={`${entry.chain_date}-${String(entry.sequence)}`}
              >
                <span>
                  {t(`marketplace.audit.${entry.action}`, { defaultValue: entry.action })}
                  <span className="lq-mk__meta"> · {personName(entry.actor)}</span>
                </span>
                <span className="lq-mk__meta">
                  {ageText(t, entry.occurred_at, now) ?? entry.occurred_at}
                </span>
              </div>
            ))}
          </section>
        ) : null}
      </div>
    </SidePanel>
  );
}
